// External Imports
import { h } from 'preact';
import { Children } from 'preact/compat';
import CarouselNuka from 'nuka-carousel';
import {
  Button,
  IconArrowLeft,
  IconArrowRight,
  Inline,
  pxToRem,
  responsiveProp,
  styled,
} from '@lessonnine/design-system.lib';

const controlsPositionValue = {
  bottomCenter: 'bottomCenter',
  bottomLeft: 'bottomLeft',
  sides: 'sides',
};

const slideEffectValue = {
  zoom: 'zoom',
};

const scaleDownFactor = 0.8;

const Slide = styled.div`
  ${({ controlsPosition }) =>
    responsiveProp(controlsPosition, (positionValue) => {
      // The slide requires to have some margin to allow for the buttons to not overlap with it
      // By default, nuka carousel have controls floating over the slides
      if (
        positionValue === controlsPositionValue.bottomCenter ||
        positionValue === controlsPositionValue.bottomLeft
      ) {
        return { margin: `0 0 ${pxToRem(60)}` };
      }
      if (positionValue === controlsPositionValue.sides) {
        return { margin: `0 ${pxToRem(60)}` };
      }
      return {};
    })}
`;

// Had to separate those slide styles out, because otherwise as we are forced to use classNames from nuka-carousel to select, they would apply to all slides from CSS, whereas we want it to apply only to the Carousel with zoom
const ZoomSlide = styled(Slide)`
  transition: transform 0.5s ease-in-out;
  .slide {
    display: flex;
  }
  /* Because of the zoom, we get quite a huge space between the current slide and the slides around it, so fixing this by the surrounding slides towards the center */
  /* For all slides, moving them to the left */
  .slide > & {
    transform: translateX(-5%) scale(${scaleDownFactor});
  }
  /* The current slide is the slide on the left of the zoomed one, bring it towards it */
  .slide-current > & {
    transform: translateX(5%) scale(${scaleDownFactor});
  }
  /* The slide after the current one is the zoomed one */
  .slide-current + .slide > & {
    transform: scale(1);
  }
`;

const ArrowButton = styled(Button)`
  ${({ controlsPosition, position }) =>
    responsiveProp(controlsPosition, (positionValue) => ({
      display: positionValue === position ? 'inline-flex' : 'none',
    }))}
`;

const renderPreviousButton = ({ previousDisabled, previousSlide }, controlsPosition, position) => (
  <ArrowButton
    color="secondary-default"
    controlsPosition={controlsPosition}
    disabled={previousDisabled}
    icon={<IconArrowLeft />}
    onClick={previousSlide}
    position={position}
    title="Previous"
  />
);

const renderNextButton = ({ nextDisabled, nextSlide }, controlsPosition, position) => (
  <ArrowButton
    color="secondary-default"
    controlsPosition={controlsPosition}
    disabled={nextDisabled}
    icon={<IconArrowRight />}
    onClick={nextSlide}
    position={position}
    title="Next"
  />
);

const renderBothButtons = (
  { nextDisabled, nextSlide, previousDisabled, previousSlide },
  controlsPosition,
  position,
) => (
  <Inline gap={16}>
    {renderPreviousButton({ previousDisabled, previousSlide }, controlsPosition, position)}
    {renderNextButton({ nextDisabled, nextSlide }, controlsPosition, position)}
  </Inline>
);

// Component Definition
/**
 * Carousel component
 *
 * @param {keyof controlsPositionValue} controlsPosition - position of the carousel controls
 * @param {keyof slideEffectValue} [slideEffect] - effect to apply to the slide
 * @param {number=1} slidesToShow - number of slides to show
 *  */
function Carousel({ children, controlsPosition, slideEffect, slidesToShow = 1 }) {
  const SlideComponent = slideEffect === slideEffectValue.zoom ? ZoomSlide : Slide;
  return (
    <CarouselNuka
      renderBottomCenterControls={(args) =>
        renderBothButtons(args, controlsPosition, controlsPositionValue.bottomCenter)
      }
      renderBottomLeftControls={(args) =>
        renderBothButtons(args, controlsPosition, controlsPositionValue.bottomLeft)
      }
      renderCenterLeftControls={(args) =>
        renderPreviousButton(args, controlsPosition, controlsPositionValue.sides)
      }
      renderCenterRightControls={(args) =>
        renderNextButton(args, controlsPosition, controlsPositionValue.sides)
      }
      slidesToShow={slidesToShow}
      wrapAround
    >
      {Children.map(children, (child) => (
        <SlideComponent controlsPosition={controlsPosition} effect={slideEffect}>
          {child}
        </SlideComponent>
      ))}
    </CarouselNuka>
  );
}

// Module Exports
export { Carousel, controlsPositionValue, slideEffectValue };
